import React, { forwardRef } from 'react';
import type { SliderItem } from 'react-compound-slider';
import type { ForwardRefType } from '@noths/polaris-dev-ts-recipes';

import { isMutableRefObject } from 'src/utils';
import { MAXIMUM_HANDLE_INDEX, MINIMUM_HANDLE_INDEX } from '../constants/handles';
import { RangeInputTextFieldsWrapper } from '../styles';
import type { HandleInputNames, RangeInputRanges, RangeInputValues } from '../types';
import { Input } from './Input';

export interface RangeInputTextFieldsProps {
  handleInputNames: HandleInputNames;
  handles: SliderItem[];
  values: RangeInputValues;
  valueTextPrefix?: string;
  range: RangeInputRanges;
  onChange: (value: RangeInputValues) => void;
}

export type RangeInputRefMap = Record<RangeInputTextFieldName, HTMLInputElement[]>;

const getNewValueArray = (currentValues: RangeInputValues, newValue: number, index: number) => {
  const newArray: RangeInputValues = [...currentValues];
  newArray[index] = newValue;
  return newArray;
};

export enum RangeInputTextFieldName {
  Minimum = 'filter[price_at_least]',
  Maximum = 'filter[price_at_most]',
}

const handleRef = (
  inputRef: HTMLInputElement | null,
  ref: ForwardRefType<RangeInputRefMap>,
  refName: RangeInputTextFieldName,
) => {
  if (inputRef && isMutableRefObject(ref)) {
    if (!ref.current[refName].includes(inputRef)) {
      ref.current[refName].push(inputRef);
    }
  }
};

export const RangeInputTextFields = forwardRef<RangeInputRefMap, RangeInputTextFieldsProps>(
  (
    {
      handleInputNames: { minimum: minInputName, maximum: maxInputName },
      handles,
      onChange,
      values,
      valueTextPrefix = '',
    }: RangeInputTextFieldsProps,
    ref,
  ) => {
    const { value: minValue } = handles[MINIMUM_HANDLE_INDEX];
    const { value: maxValue } = handles[MAXIMUM_HANDLE_INDEX];

    return (
      <RangeInputTextFieldsWrapper>
        <Input
          value={minValue}
          name={minInputName}
          onChange={(e) =>
            onChange(getNewValueArray(values, Number(e.target.value), MINIMUM_HANDLE_INDEX))
          }
          handleRef={(inputRef) => handleRef(inputRef, ref, RangeInputTextFieldName.Minimum)}
          valueTextPrefix={valueTextPrefix}
        />
        <Input
          value={maxValue}
          name={maxInputName}
          onChange={(e) =>
            onChange(getNewValueArray(values, Number(e.target.value), MAXIMUM_HANDLE_INDEX))
          }
          handleRef={(inputRef) => handleRef(inputRef, ref, RangeInputTextFieldName.Maximum)}
          valueTextPrefix={valueTextPrefix}
        />
      </RangeInputTextFieldsWrapper>
    );
  },
);
